import { Component, CUSTOM_ELEMENTS_SCHEMA, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { MRSearchService } from '../../../services/common.services';
import { HybridSearchResult, HybridSearchResponse } from '../../../models/mrsearch.models';
import { MarkdownModule } from 'ngx-markdown';
import { MODALITY_PRODUCT_MAP } from './product-list';
import '@shui/core/sh-page';
import '@shui/core/sh-access-bar';
import '@shui/core/sh-pane';
import '@shui/core/sh-card';
import '@shui/core/sh-text';
import '@shui/core/sh-divider';
import '@shui/core/sh-dropdown';
import '@shui/core/sh-menu-item';
import '@shui/core/sh-input-text';
import '@shui/core/sh-button';
import '@shui/core/sh-spinner';
import '@shui/core/sh-box';
import '@shui/core/sh-accordion';
import '@shui/core/sh-list';

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [MarkdownModule, CommonModule],
  providers: [MRSearchService],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class SearchComponent implements OnDestroy {
  title = 'MR Search';
  showSpinner = false;
  showResponse = false;
  queryInput = '';
  selectedModality = 'All';
  selectedProduct = 'All';
  showValidationMessage = false;
  validationMessage = '';
  modalityError = false;
  productError = false;

  searchError = '';

  hybridResults: HybridSearchResult[] = [];

  modalityProductMap = MODALITY_PRODUCT_MAP;
  modalities: string[] = ['All', ...Object.keys(MODALITY_PRODUCT_MAP)];

  filteredProducts: string[] = [];
  visitedResults: Set<number> = new Set();
  isModalitySupported = true;

  private readonly supportedModalities = new Set(['Site Planning Guides', 'AT', 'CT', 'MI', 'MR', 'US', 'XP']);
  private readonly subscriptions: Subscription[] = [];

  constructor(private readonly mrSearchService: MRSearchService) {
    this.updateFilteredProducts();
  }
  
  fillInput(text: string): void {
    this.queryInput = text;
    this.showValidationMessage = false;
    this.showResponse = false;
  }
  
  clearInput(): void {
    this.queryInput = '';
    this.showResponse = false;
    this.searchError = '';
    this.hybridResults = [];
    this.visitedResults.clear();
    this.selectedModality = 'All';
    this.selectedProduct = 'All';
    this.modalityError = false;
    this.productError = false;
    this.isModalitySupported = true;
    this.updateFilteredProducts();
  }

  onModalityChange(modality: string): void {
    this.selectedModality = modality;
    this.modalityError = false;
    this.selectedProduct = 'All';
    this.isModalitySupported = modality === 'All' || this.supportedModalities.has(modality);
    this.updateFilteredProducts();
  }

  onProductChange(product: string): void {
    this.selectedProduct = product;
    this.productError = false;
  }

  markAsVisited(index: number): void {
    this.visitedResults.add(index);
  }

  updateFilteredProducts(): void {
    if (this.selectedModality === 'All') {
      const allProducts: string[] = [];
      for (const key of Object.keys(this.modalityProductMap)) {
        allProducts.push(...this.modalityProductMap[key]);
      }
      this.filteredProducts = ['All', ...allProducts];
    } else {
      const products = this.modalityProductMap[this.selectedModality] || [];
      this.filteredProducts = ['All', ...products];
    }
    this.selectedProduct = 'All';
  }

  validateDropdowns(): boolean {
    this.modalityError = !this.selectedModality;
    this.productError = !this.selectedProduct;
    return !this.modalityError && !this.productError;
  }

  validateInput(): boolean {
    this.showValidationMessage = false;
    this.validationMessage = '';

    if (!this.validateDropdowns()) {
      return false;
    }

    if (!this.queryInput || this.queryInput.trim() === '') {
      this.validationMessage = 'Please enter your query';
      this.showValidationMessage = true;
      return false;
    }

    return true;
  }
  
  processInput(): void {
    if (!this.validateInput()) {
      return;
    }
    
    this.showSpinner = true;
    this.searchError = '';
    this.hybridResults = [];
    this.visitedResults.clear();

    const filterExpression = this.mrSearchService.buildProductFilter(this.selectedModality, this.selectedProduct);

    const subscription = this.mrSearchService.hybridSearch({
      query: this.queryInput.trim(),
      top: 10,
      filter_expression: filterExpression,
      k_nearest_neighbors: 50,
      product_title: this.selectedProduct !== 'All' ? this.selectedProduct : ''
    }).subscribe({
      next: (response: HybridSearchResponse) => {
        if (response.success) {
          this.hybridResults = response.results;
          this.showResponse = true;
          this.searchError = '';
        } else {
          this.searchError = response.error || 'An error occurred during search';
          this.showResponse = true;
        }
        this.showSpinner = false;
      },
      error: (error: any) => {
        console.error('Hybrid Search Error:', error);
        this.searchError = error.message || 'Failed to perform search. Please try again.';
        this.showResponse = true;
        this.showSpinner = false;
      }
    });

    this.subscriptions.push(subscription);
  }
  
  onQueryInput(event: any): void {
    this.queryInput = event.target.value;
    this.showValidationMessage = false;
  }
  
  hasResults(): boolean {
    return this.hybridResults && this.hybridResults.length > 0;
  }
  
  hasError(): boolean {
    return this.searchError !== '';
  }
  
  getHybridResultLabel(result: HybridSearchResult, index: number): string {
    const title = (result.productTitle ? result.productTitle + ' - ' : '') + (result.document_title || 'Unknown Document');
    return `Result ${index + 1}: ${title}`;
  }
  
  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  truncateText(text: string, maxLength: number): string {
    if (!text || text.length <= maxLength) {
      return text;
    }
    return text.substring(0, maxLength).trim() + '...';
  }

  private toTitleCase(text: string): string {
    if (!text) return text;
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  getScoreLabel(score: unknown): 'excellent' | 'good' | 'average' | 'poor' | 'unknown' {
    if (typeof score === 'string') {
      switch (score.trim().toLowerCase()) {
        case 'excellent':
          return 'excellent';
        case 'good':
          return 'good';
        case 'average':
          return 'average';
        case 'poor':
          return 'poor';
        default:
          return 'unknown';
      }
    }

    const numericScore = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(numericScore)) {
      return 'unknown';
    }

    if (numericScore >= 0.85) return 'excellent';
    if (numericScore >= 0.7) return 'good';
    if (numericScore >= 0.5) return 'average';
    return 'poor';
  }

  getScoreText(score: unknown): string {
    const label = this.getScoreLabel(score);
    const labelText = this.toTitleCase(label);

    if (typeof score === 'string') {
      const trimmed = score.trim();
      return trimmed ? labelText : 'N/A';
    }

    const numericScore = typeof score === 'number' ? score : Number(score);
    if (!Number.isFinite(numericScore)) {
      return 'N/A';
    }

    return `${labelText} (${(numericScore * 100).toFixed(1)}%)`;
  }

  getScoreClass(score: unknown): string {
    switch (this.getScoreLabel(score)) {
      case 'excellent':
        return 'score-excellent';
      case 'good':
        return 'score-good';
      case 'average':
        return 'score-average';
      case 'poor':
        return 'score-poor';
      default:
        return 'score-unknown';
    }
  }
}
